/**
 * UsageWidget.jsx — Widget compacto de consumo (C3 PASSO 4).
 *
 * Usado em CoachHomePage (EQUIPE) e ManagerHomePage (TIME).
 *
 * UI_PADRAO compliant:
 *   - Cinza-base, sem cor decorativa
 *   - Tabler/lucide outline
 *   - font-medium/semibold
 *   - Cota = info; vermelho #a93333 SO se perto de saturar (>=85%)
 *
 * Comportamento:
 *   - loading: skeleton
 *   - 403/null: NAO renderiza (caller decide; aqui sumimos silencioso)
 *   - data ok: mostra "X de Y · plano" + link "ver detalhes"
 *   - sem subscription: mostra so "X interacoes"
 */
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Activity, Users, Building2, ArrowRight, AlertTriangle } from 'lucide-react';

const fmt = (n, lang) =>
  n == null ? '—' : Number(n).toLocaleString(lang === 'en' ? 'en-US' : 'pt-BR');

/**
 * @param {Object} props
 * @param {'team' | 'club'} props.scope
 * @param {Function} props.fetcher                   - async function () => data (do service)
 * @param {string}  props.detailHref                 - '/meu-consumo/equipe' ou '/meu-consumo/club'
 * @param {'pt'|'en'} [props.lang='pt']
 */
const UsageWidget = ({ scope, fetcher, detailHref, lang = 'pt' }) => {
  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(true);
  const [hidden, setHidden]   = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    fetcher()
      .then((d) => {
        if (cancelled) return;
        if (!d) { setHidden(true); return; }
        setData(d);
      })
      .catch(() => { if (!cancelled) setHidden(true); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [fetcher]);

  if (hidden) return null;

  // Labels
  const L = {
    pt: {
      teamTitle:  'Consumo da equipe',
      clubTitle:  'Consumo do clube',
      teamSubtitle: 'Atletas vinculados a você',
      clubSubtitle: 'Todos do clube',
      interactions: 'interações',
      thisMonth: 'este mês',
      ofTotal:   'de',
      noLimit:   'limite por usuário não definido',
      viewDetail: 'Ver detalhes',
      athletes:  'atletas',
      activeAthletes: 'ativos este mês',
      fallback:  'inclui consumo recuperado por fallback',
    },
    en: {
      teamTitle:  'Team usage',
      clubTitle:  'Club usage',
      teamSubtitle: 'Athletes linked to you',
      clubSubtitle: 'All from your club',
      interactions: 'interactions',
      thisMonth: 'this month',
      ofTotal:   'of',
      noLimit:   'no individual limit defined',
      viewDetail: 'View details',
      athletes:  'athletes',
      activeAthletes: 'active this month',
      fallback:  'includes recovered usage via fallback',
    },
  }[lang] || {};

  if (loading) {
    return (
      <div className="bg-white border border-gray-200 rounded-lg p-4 animate-pulse">
        <div className="h-3 w-32 bg-gray-200 rounded mb-3" />
        <div className="h-6 w-40 bg-gray-200 rounded mb-2" />
        <div className="h-3 w-24 bg-gray-200 rounded" />
      </div>
    );
  }

  if (!data) return null;

  const isTeam = scope === 'team';
  const Icon = isTeam ? Users : Building2;
  const title = isTeam ? L.teamTitle : L.clubTitle;
  const subtitle = isTeam ? L.teamSubtitle : L.clubSubtitle;
  const interactions = data?.usage_current_month?.interactions ?? 0;

  // Subscription so existe na visao TIME (club)
  const sub = data?.subscription;
  const limit = sub?.ai_credits_monthly;
  const planLabel = sub?.plan_key;
  const pct = limit ? (interactions / limit) * 100 : null;
  const isWarning = pct != null && pct >= 85;

  // Visao EQUIPE: extras
  const athletesTotal = data?.team?.athletes_total;
  const athletesActive = data?.team?.athletes_active;

  // Visao TIME: fallback flag
  const fallbackApplied = data?.usage_current_month?.fallback_applied;

  return (
    <div className="bg-white border border-gray-200 rounded-lg p-4 hover:border-gray-300 transition-colors">
      {/* Header */}
      <div className="flex items-start justify-between gap-3 mb-3">
        <div className="flex items-center gap-2 min-w-0">
          <div className="w-8 h-8 rounded-md bg-gray-100 flex items-center justify-center flex-shrink-0">
            <Icon size={16} className="text-gray-700" />
          </div>
          <div className="min-w-0">
            <div className="text-sm font-semibold text-gray-900 truncate">{title}</div>
            <div className="text-xs text-gray-500 truncate">{subtitle}</div>
          </div>
        </div>
        {isWarning && (
          <AlertTriangle size={16} style={{ color: '#a93333' }} className="flex-shrink-0" title={`${pct.toFixed(0)}% do limite`} />
        )}
      </div>

      {/* Metrica principal */}
      <div className="mb-3">
        <div className="flex items-baseline gap-2">
          <span className="text-2xl font-bold text-gray-900">
            {fmt(interactions, lang)}
          </span>
          {limit ? (
            <span className="text-sm font-medium text-gray-500">
              {L.ofTotal} {fmt(limit, lang)}
            </span>
          ) : (
            <span className="text-xs font-medium text-gray-400">
              {L.interactions} {L.thisMonth}
            </span>
          )}
        </div>
        {limit && (
          <div className="text-xs text-gray-500 mt-1">
            {L.interactions} {L.thisMonth}
            {planLabel && <span className="text-gray-400"> · {planLabel}</span>}
          </div>
        )}
      </div>

      {/* Barra de progresso (so se tem limite) */}
      {limit && (
        <div className="mb-3">
          <div className="w-full bg-gray-100 rounded-full h-1.5 overflow-hidden">
            <div
              className="h-full rounded-full transition-all"
              style={{
                width: `${Math.min(pct, 100)}%`,
                backgroundColor: isWarning ? '#a93333' : '#6b7280',
              }}
            />
          </div>
        </div>
      )}

      {/* Extras por scope */}
      {isTeam && athletesTotal != null && (
        <div className="text-xs text-gray-500 mb-3">
          <Activity size={11} className="inline -mt-0.5 mr-1 text-gray-400" />
          <span className="font-medium text-gray-700">{athletesTotal}</span> {L.athletes}
          {athletesActive > 0 && (
            <span className="text-gray-400">
              {' '}· {athletesActive} {L.activeAthletes}
            </span>
          )}
        </div>
      )}
      {!isTeam && fallbackApplied && (
        <div className="text-[11px] text-gray-400 mb-3 italic">
          {L.fallback}
        </div>
      )}

      {/* Link detalhe */}
      <Link
        to={detailHref}
        className="inline-flex items-center gap-1 text-xs font-semibold text-gray-700 hover:text-gray-900 transition-colors"
      >
        {L.viewDetail}
        <ArrowRight size={12} />
      </Link>
    </div>
  );
};

export default UsageWidget;
